import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceArea, Legend,
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import { StoreBadge } from './AppRadar';

/*
 * MultiPay yorum analizi — sadece Multinet markası.
 * Sebep sınıflandırması 22 Eyl 2026'da App Radar'ın topladığı 1–3★ yorumların
 * (427 adet) tek tek okunmasıyla yapıldı; o yüzden sayılar DONDURULMUŞ.
 * Üstteki KPI şeridi ise canlı: /api/app-radar/apps'ten gelir.
 */

const API = 'https://api.glowytics.com/api/app-radar';
const GOOGLE_ID = 'com.mobisoft.multimobil';
const APPLE_ID = '680381668';
const AS_OF = '22 Eylül 2026';

// Android, aylık: [ay, toplam yorum, 1–2★]
const MONTHLY = [
  ['2024-04', 21, 6], ['2024-05', 23, 7], ['2024-06', 15, 3], ['2024-07', 14, 2], ['2024-08', 23, 9],
  ['2024-09', 23, 8], ['2024-10', 10, 3], ['2024-11', 13, 4], ['2024-12', 21, 3], ['2025-01', 16, 4],
  ['2025-02', 24, 11], ['2025-03', 19, 5], ['2025-04', 20, 9], ['2025-05', 29, 12], ['2025-06', 30, 17],
  ['2025-07', 19, 10], ['2025-08', 7, 4], ['2025-09', 7, 6], ['2025-10', 10, 8], ['2025-11', 14, 9],
  ['2025-12', 19, 13], ['2026-01', 35, 17], ['2026-02', 26, 10], ['2026-03', 15, 4], ['2026-04', 18, 13],
  ['2026-05', 13, 6], ['2026-06', 3, 2], ['2026-07', 7, 1], ['2026-08', 5, 2], ['2026-09', 1, 0],
].map(([m, n, neg]) => ({ m, other: n - neg, neg }));

const TR_MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const monthLabel = (m) => { const [y, mo] = m.split('-'); return `${TR_MONTHS[+mo - 1]} ${y.slice(2)}`; };

const SCORE_DIST = {
  google: { 1: 180, 2: 28, 3: 33, 4: 33, 5: 226 },
  apple: { 1: 230, 2: 42, 3: 37, 4: 17, 5: 174 },
};

const INCIDENTS = [
  { from: '2025-05', to: '2025-07', label: 'Güncelleme: uygulama İngilizce\'de takılı, özellikler kayboldu' },
  { from: '2025-11', to: '2026-01', label: '"Sözleşme güncellendi" ekranında çökme' },
  { from: '2026-04', to: '2026-04', label: 'Ay başı toplu bağlantı hatası' },
];

// n: Android 1–3★ (241 yorum, Nis 2024 – Eyl 2026), ios: App Store 1–3★ (47 yorum, May 2025 – Eyl 2026).
// Elle sınıflandırma; bir yorum birden fazla sebebe girebilir.
const REASONS = [
  {
    key: 'stability', n: 70, ios: 13, iosQuotes: ["Sürekli açılmıyor ve kredi kartıyla ödemem gerekiyor ödemelerimi", "Markette ödeme yapacağım an uygulamanız açılmıyor kart gönderin."], title: 'Uygulama açılmıyor, çöküyor, "bağlantı hatası"',
    body: 'Tek başına en büyük sebep. Neredeyse hepsi ödeme anında: kullanıcı kasada ya da restoranda kalıyor, parayı kendi cebinden ödüyor. Maaş günü ve ay başında yoğunlaşması yük kaynaklı bir sorunu düşündürüyor.',
    quotes: [
      'berbat bir uygulama, sürekli çöküyor. bağlantı hatası verip duruyor. restoran kasasında mağdur kaldım resmen.',
      'Yemek saati uygulama çöktü komple olacak iş değil. Böyle bir sistem olamaz. multinetten ödeyeceğimize kredi kartı kullanacaksak ne anlamı var?',
      'her maaş günü işlem yapamama sisteme geç düşmesi son bi senedir berbatsınız',
    ],
  },
  {
    key: 'network', n: 26, ios: 3, iosQuotes: ["Büyükşehirleri bilmem ama küçük şehirlerde geçerli olduğu market restoran çok yetersiz daha fazla yer ile anlaşma yapmaları gerekiyor"], title: 'Kartın geçtiği yer az, işyeri kabul etmiyor',
    body: 'En sık istek zincir marketler (BİM, A101, ŞOK). Anlaşmalı yerlerin pahalı olması, bazı işyerlerinin Multinet ile ödemede indirimi iptal etmesi ya da fark alması, uygulamada "geçiyor" görünen yerde geçmemesi. Rakip adıyla kıyaslama var.',
    quotes: [
      'BİM ŞOK A 101 bu marketlerde geçmiyo sodexo pluxe bile geçiyo multinet geçmiyo',
      'Multinet kullanacağımda indirim, kampanya gibi şeyleri iptal ediyor kartta geçiyor, multinette geçmiyor vs. diye.',
    ],
  },
  {
    key: 'update', n: 18, ios: 3, iosQuotes: ["Önceden hesabıma yüklenecek bakiyeyi 1-2 gün önceden görebiliyordum şimdi o özelliği kaldırmışlar"], title: 'Güncellemenin bozdukları',
    body: 'Mayıs–Haziran 2025 güncellemesinden sonra dil Türkçe seçili olduğu halde İngilizce kaldı (8 yorum). Bekleyen bakiye, Tatlı Para / "bugün buradaydım" düğmesi ve doğrudan ödeme kısayolu kayboldu. "Eski sürüm daha iyiydi" sık tekrar ediliyor.',
    quotes: [
      'Türkçe dil desteği kaldırılmış. Dil olarak Türkçe seçili olduğu halde uygulama İngilizce çalışıyor.',
      'Bir önceki versiyonda direk ödeme ekranına yönlendiren bir action vardı. Pratik oluyordu. Geri getirin lütfen',
    ],
  },
  {
    key: 'support', n: 17, ios: 2, iosQuotes: ["1 senedir sorunsuz kullanıyordum cihaz kaynaklı diyerek akıl verdiler ve yaklaşık 2 aydır giriş yapamıyorum"], title: 'Destek ulaşılamıyor, çözüm gelmiyor',
    body: 'Çağrı merkezine ulaşılamaması, WhatsApp\'tan otomatik mesaj dışında cevap gelmemesi, "teknik birime ilettik" deyip dönüş olmaması. Mağazadaki cevapların kalıp metin olduğu da ayrıca eleştiriliyor.',
    quotes: [
      'müşteri hizmetlerine bağlanmak Mars\'a gitmekten daha zor. uygulamanızı daha pratik yapın',
      'whatsapptan otomatik mesaj dışında cevap veren yok arıyorum müşteri hizmetlerine ulaşmıyorum.',
    ],
  },
  {
    key: 'qr', n: 14, ios: 17, iosQuotes: ["Qr ödeme de kamerayı yüklemiyor? Güncelleme yaptım uygulamayı silip yükledim kamera izninini açıp kapattım. ios versiyonum 26.2", "her gün kullandığım uygulama dün iphoneumu güncelledim bugün kamera açılmıyor silip geri yükledim hala aynı"], title: 'Karekod, NFC ve kamera',
    body: 'QR ekranı açılmıyor, kamera yakına odaklanmıyor, POS ekranındaki kodu okumuyor. Karekodla ödemeyi açarken doğum tarihinin ay ay geri gidilerek seçilmesi kasada kuyruk yaratıyor.',
    quotes: [
      'ekrandaki karekod küçük ve buna karşılık kameranın yakınlaştırma fonksiyonunu devre dışı bırakmışlar.',
      '42 yaşindayım yani 42 x12= 484 kere tıklamam gerekti. Kasada dedeler teyzeler söylenmeye başladılar.',
    ],
  },
  {
    key: 'map', n: 12, ios: 3, iosQuotes: ["Konumda marketi seçiyorum manava getiriyor"], title: 'Harita ve konumlar yanlış',
    body: 'Gösterilen yerde işyeri yok, konumlar 1,5–2 km sapıyor, market filtresi alakasız işyerleri getiriyor. Kullanıcıların gönderdiği konum düzeltmeleri uygulanmıyor.',
    quotes: [
      'kahve diyarı yazıyordu Üsküdar\'da. gittim . müftülük vardı. kahve diyarı yoktu.',
      'Restoranın konumu yanlış diye geri bildirim gönderiyorum, şaşma payı olabilir diyip düzeltmiyorlar.',
    ],
  },
  {
    key: 'login', n: 12, ios: 4, iosQuotes: ["Numarayı giriyorum giriş kodu gelene kadar süre doluyor güncelleme şart"], title: 'Kayıt, giriş, SMS kodu, aktivasyon',
    body: 'Doğrulama kodunun gelmemesi, telefon numarası değişince hesaba girilememesi, yeni kartın aktif edilememesi.',
    quotes: ['uygulamayı ilk defa kullaancagim kayıt olurken doğrulama kodu gelmedi 3 defa denedim lütfen biri yardımcı olsun'],
  },
  {
    key: 'money', n: 11, ios: 3, iosQuotes: ["Kart ekleme limiti ne demek ya? Sırf şu saçma limit yüzünden bir gün kart ekleyemedim yemek alamadım"], title: 'Paraya erişim: bloke, limit, nakit, aktarım',
    body: 'İşten ayrılınca bakiyeye bloke konması, kart ekleme limiti, bakiyenin nakde çevrilememesi, kartlar arasında bakiye birleştirilememesi.',
    quotes: ['1850 TL civarı bir parama bloke koydular işten ayrıldığım için?'],
  },
  {
    key: 'topup', n: 7, ios: 0, iosQuotes: [], title: 'Para yükleme ekranı çalışmıyor',
    body: 'Tutarı girince ana ekrana atıyor, işlem askıda kalıyor; aylarca sürdüğü yazılıyor.',
    quotes: ['Uygulamanın bir süredir para yükle ekranı çalışmıyor, tutarı girip yükleme yapmaya çalıştığımda uygulama beni ana ekrana atıyor ve işlem askıda kalıyor. Yaklaşık 2 aydır var bu sorun ve asla düzelmiyor.'],
  },
  {
    key: 'privacy', n: 2, ios: 0, iosQuotes: [], title: 'Mahremiyet: bakiye ve harcama geçmişi açıkta',
    body: 'Sayısı az ama etkisi büyük: ana sayfada harcama geçmişi ve bakiyenin görünmesi, ödeme anında kalan bakiyenin karşı tarafa görünmesi.',
    quotes: ['Patron yemek parasi yuklediginde ne kadar para oldugunu goruyor. Ve sonra is yerinde duyuluyor. [...] Maasa zam oranini bile etkiliyor bu durum.'],
  },
];

// iOS, 2019 – Mayıs 2025 arası yorumlardan öne çıkanlar (ilk okumadaki 186 yorum).
const IOS_OLDER = [
  ['iPhone 14 Pro kamerası QR\'a odaklanmıyor (2022–2023)', 10],
  ['Yanlış konum ve kullanışsız harita', 9],
  ['SMS / doğrulama kodu gelmiyor', 8],
  ['Harcama geçmişi görünmüyor', 7],
  ['Kasım 2023 güncellemesinden sonra QR ekranı siyah', 6],
  ['Tek kart sınırı, kartlar arası aktarım yok', 6],
];

const fmt = (n) => (n == null ? '–' : Number(n).toLocaleString('tr-TR'));
const fmtR = (n) => (n == null ? '–' : Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const Card = ({ children, className = '' }) => (
  <div className={`bg-white border border-[#E5E7EB] rounded-xl shadow-[0_1px_2px_rgba(16,24,40,0.04)] ${className}`}>{children}</div>
);

export default function MultipayReviewAnalysis() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || '';
  const [live, setLive] = useState({});

  useEffect(() => {
    if (!brandId) return;
    fetch(`${API}/apps`, { headers: { 'X-Brand-ID': brandId }, credentials: 'include' })
      .then(r => r.json())
      .then(j => {
        const byId = {};
        for (const a of j.apps || []) byId[a.store_app_id] = a;
        setLive({ google: byId[GOOGLE_ID], apple: byId[APPLE_ID] });
      })
      .catch(() => {});
  }, [brandId]);

  const reasons = useMemo(() => [...REASONS].sort((a, b) => (b.n + b.ios) - (a.n + a.ios)), []);
  const maxReason = Math.max(...REASONS.map(r => Math.max(r.n, r.ios)));
  const dist = useMemo(() => Object.fromEntries(Object.entries(SCORE_DIST).map(([s, d]) => {
    const total = Object.values(d).reduce((a, b) => a + b, 0);
    return [s, { total, negPct: Math.round(((d[1] + d[2]) / total) * 100), d }];
  })), []);

  return (
    <div className="min-h-full bg-[#F6F8FF] p-6 text-[#111827]" style={{ fontVariantNumeric: 'tabular-nums' }}>
      <div className="max-w-[1200px] mx-auto space-y-6">
        {/* Başlık */}
        <div>
          <div className="text-xs font-medium text-[#436AEB]">Social Intelligence · App Radar</div>
          <h1 className="text-2xl font-semibold mt-1">MultiPay yorum analizi</h1>
          <p className="text-sm text-[#6B7280] mt-1 max-w-3xl">
            MultiPay'in Google Play ve App Store yorumlarında kullanıcılar neden şikâyet ediyor?
            {' '}Olumsuz yorumların hepsi tek tek okunup sebeplerine göre sınıflandırıldı.
          </p>
        </div>

        {/* Ana bulgu */}
        <Card className="p-5 border-l-4 border-l-[#436AEB]">
          <div className="text-base font-semibold">
            Kullanıcıların asıl şikâyeti tasarım değil, kasada uygulamaya güvenememek.
          </div>
          <p className="text-sm text-[#374151] mt-2 leading-relaxed">
            Mağaza puanı yüksek (Android 4,64 · iOS 4,82) ama yazılı yorumların <b>%42–54'ü 1–2★</b>.
            {' '}Olumsuz yorumların en büyük kısmı uygulamanın ödeme anında açılmaması ya da hata vermesi:
            {' '}kartta para var, kullanıcı kasada kalıyor. Kartın geçtiği yerlerin darlığı ve cevapsız
            {' '}destek eklenince 1★ geliyor. iOS'ta tablo son bir yılda daha kötü: Mayıs 2025'ten bu yana
            {' '}gelen 50 yorumun 43'ü 1–2★, en sık şikâyet QR kameranın açılmaması.
          </p>
        </Card>

        {/* Canlı KPI */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { key: 'google', label: 'Google Play' },
            { key: 'apple', label: 'App Store' },
          ].map(({ key, label }) => {
            const a = live[key];
            const d = dist[key];
            return (
              <Card key={key} className="p-5">
                <div className="flex items-center gap-3">
                  {a?.icon_url && <img src={a.icon_url} alt="" className="w-10 h-10 rounded-xl" />}
                  <div>
                    <div className="text-sm font-semibold">MultiPay · {label}</div>
                    <div className="text-xs text-[#9CA3AF]">Mağaza verisi canlı, her gece güncelleniyor</div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3 mt-4">
                  <Kpi label="Mağaza puanı" value={fmtR(a?.rating)} />
                  <Kpi label="Puan sayısı" value={fmt(a?.rating_count)} />
                  <Kpi label="Yazılı yorum ort." value={fmtR(Object.entries(d.d).reduce((s, [k, v]) => s + k * v, 0) / d.total)} />
                  <Kpi label="1–2★ oranı" value={`%${d.negPct}`} accent />
                </div>
                <ScoreBar d={d.d} total={d.total} />
                <div className="text-xs text-[#9CA3AF] mt-2">
                  {key === 'google'
                    ? `${fmt(d.total)} yorum · Nis 2024 – Eyl 2026 · olumsuz yorumların 169/208'ine cevap verilmiş`
                    : `${fmt(d.total)} yorum · Kas 2017 – Eyl 2026 · Apple akışı firma cevaplarını içermiyor`}
                </div>
              </Card>
            );
          })}
        </div>

        {/* Zaman çizelgesi */}
        <Card className="p-5">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h2 className="text-base font-semibold">Olumsuz yorumlar ay ay (Android)</h2>
            <div className="text-xs text-[#6B7280]">Gölgeli alanlar yorumlarda adı geçen olaylar</div>
          </div>
          <div className="h-[260px] mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={MONTHLY} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="m" tickFormatter={monthLabel} tick={{ fontSize: 11, fill: '#6B7280' }} interval={2} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} allowDecimals={false} axisLine={false} tickLine={false} />
                {INCIDENTS.map(i => (
                  <ReferenceArea key={i.from} x1={i.from} x2={i.to} fill="#436AEB" fillOpacity={0.07} ifOverflow="extendDomain" />
                ))}
                <Tooltip
                  labelFormatter={monthLabel}
                  formatter={(v, n) => [v, n]}
                  contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="neg" name="1–2★" stackId="a" fill="#EF4444" />
                <Bar dataKey="other" name="3–5★" stackId="a" fill="#C7D2FE" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            {INCIDENTS.map(i => (
              <div key={i.from} className="rounded-lg bg-[#EEF2FE] px-3 py-2">
                <div className="text-xs font-semibold text-[#436AEB]">
                  {monthLabel(i.from)}{i.to !== i.from ? ` – ${monthLabel(i.to)}` : ''}
                </div>
                <div className="text-sm text-[#374151] mt-0.5">{i.label}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#9CA3AF] mt-3">
            Haziran–Eylül 2026'da toplam 5 olumsuz yorum var; ancak bu dönemde gelen yorum sayısı da düşük.
          </p>
        </Card>

        {/* Sebepler */}
        <Card className="p-5">
          <h2 className="text-base font-semibold">Olumsuz yorumların sebepleri</h2>
          <p className="text-xs text-[#6B7280] mt-1 flex items-center gap-1.5 flex-wrap">
            <StoreBadge store="google" size={13} /> 241 adet 1–3★ yorum (Nis 2024 – Eyl 2026)
            <span className="mx-1">·</span>
            <StoreBadge store="apple" size={13} /> 47 adet 1–3★ yorum (May 2025 – Eyl 2026)
            <span className="mx-1">·</span> sayılar yaklaşık, bir yorum birden fazla sebebe girebilir
          </p>
          <div className="mt-5 space-y-5">
            {reasons.map((r, i) => (
              <div key={r.key} className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 pb-5 border-b border-[#F3F4F6] last:border-0 last:pb-0">
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs font-semibold text-[#9CA3AF] w-5">{i + 1}</span>
                    <span className="text-sm font-semibold">{r.title}</span>
                  </div>
                  {[['google', r.n], ['apple', r.ios]].map(([st, n]) => (
                    <div key={st} className="flex items-center gap-2 mt-2 ml-7">
                      <StoreBadge store={st} size={14} />
                      <div className="flex-1 h-2 bg-[#F3F4F6] rounded">
                        <div className={`h-2 rounded ${st === 'google' ? 'bg-[#436AEB]' : 'bg-[#1CA7F8]'}`} style={{ width: `${(n / maxReason) * 100}%` }} />
                      </div>
                      <span className="text-sm font-semibold w-10 text-right">{n ? `~${n}` : '–'}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-sm text-[#374151] leading-relaxed">{r.body}</p>
                  <div className="mt-2 space-y-1.5">
                    {[...r.quotes.map(q => ['google', q]), ...r.iosQuotes.map(q => ['apple', q])].map(([st, q]) => (
                      <div key={q} className="flex gap-2 text-sm text-[#6B7280] border-l-2 border-[#E5E7EB] pl-3">
                        <span className="mt-0.5"><StoreBadge store={st} size={13} /></span>
                        <span className="italic">“{q}”</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <LatestNegative live={live} brandId={brandId} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* iOS */}
          <Card className="p-5">
            <h2 className="text-base font-semibold flex items-center gap-2"><StoreBadge store="apple" size={18} /> App Store: önceki dönem</h2>
            <p className="text-xs text-[#6B7280] mt-1">
              2019 – Mayıs 2025 arası 1–3★ yorumlardan öne çıkanlar · son dönem yukarıdaki sebeplerde
            </p>
            <ThemeList items={IOS_OLDER} max={10} />
          </Card>

          {/* Öncelikler */}
          <Card className="p-5">
            <h2 className="text-base font-semibold">Yorumlardan çıkan öncelikler</h2>
            <ol className="mt-3 space-y-3 text-sm text-[#374151] list-decimal pl-5">
              <li><b>Ödeme anındaki kararlılık.</b> Açılış, bağlantı hatası ve QR ekranı; özellikle maaş günü ve ay başı yükü. Şikâyetlerin en büyük kısmı burada.</li>
              <li><b>iOS'ta QR kamera.</b> Son bir yılın iOS şikâyetlerinin en büyüğü; güncellemelerden (uygulama ya da iOS) sonra kamera açılmıyor. 2022–2023'te de aynı ekran sorun çıkarmıştı.</li>
              <li><b>Değişiklik öncesi test.</b> Android'deki üç büyük olumsuz yorum dalgasından ikisi bir değişikliğin hemen ardından başladı: güncellemeyle gelen dil hatası ve yeni sözleşme ekranındaki çökme.</li>
              <li><b>İşyeri verisinin doğruluğu.</b> Yanlış konum ve "geçiyor görünüp geçmeyen" yerler, kart kabul ağının darlığı algısını büyütüyor.</li>
              <li><b>Bakiyenin gizlenebilmesi.</b> Az sayıda ama güçlü bir istek: ana sayfada bakiye ve harcama geçmişi.</li>
            </ol>
          </Card>
        </div>

        <p className="text-xs text-[#9CA3AF]">
          Kaynak: App Radar'ın Google Play (TR) ve App Store (TR) mağazalarından topladığı yorumlar.
          {' '}Sebep sınıflandırması {AS_OF} itibarıyla yapıldı; yeni yorumlar bu sınıflandırmaya kendiliğinden eklenmez.
          {' '}App Store'un herkese açık yorum akışı ülke başına yalnız ~500 yorum verir ve firma cevaplarını içermez; iOS'ta cevap oranı bu yüzden ölçülemiyor.
        </p>
      </div>
    </div>
  );
}

function LatestNegative({ live, brandId }) {
  const [tab, setTab] = useState('apple');
  const [rows, setRows] = useState({});
  useEffect(() => {
    const app = live[tab];
    if (!app?.id || rows[tab]) return;
    const h = { headers: { 'X-Brand-ID': brandId }, credentials: 'include' };
    Promise.all([1, 2].map(sc => fetch(`${API}/app/${app.id}/reviews?score=${sc}&limit=15`, h).then(r => r.json())))
      .then(res => {
        const all = res.flatMap(j => j.reviews || [])
          .sort((a, b) => new Date(b.reviewed_at) - new Date(a.reviewed_at)).slice(0, 15);
        setRows(prev => ({ ...prev, [tab]: all }));
      })
      .catch(() => {});
  }, [tab, live, brandId, rows]);
  const list = rows[tab];
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base font-semibold">Son olumsuz yorumlar</h2>
          <p className="text-xs text-[#6B7280] mt-1">Canlı · 1–2★ · her gece yeni yorumlar eklenir</p>
        </div>
        <div className="inline-flex rounded-lg border border-[#E5E7EB] overflow-hidden text-sm">
          {[['apple', 'App Store'], ['google', 'Google Play']].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-3 py-1.5 inline-flex items-center gap-1.5 ${tab === k ? 'bg-[#EEF2FE] text-[#436AEB] font-medium' : 'bg-white text-[#6B7280] hover:bg-[#F9FAFB]'}`}>
              <StoreBadge store={k} size={14} />{l}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-4 divide-y divide-[#F3F4F6]">
        {!list && <div className="text-sm text-[#9CA3AF] py-3">Yükleniyor…</div>}
        {list && !list.length && <div className="text-sm text-[#9CA3AF] py-3">Yorum yok.</div>}
        {(list || []).map(r => (
          <div key={r.review_id} className="py-3">
            <div className="flex items-center gap-2 text-xs text-[#6B7280]">
              <span className="text-[#DC2626] font-semibold">{'★'.repeat(r.score)}<span className="text-[#E5E7EB]">{'★'.repeat(5 - r.score)}</span></span>
              <span>{new Date(r.reviewed_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              {r.version && <span>· v{r.version}</span>}
            </div>
            {r.title && <div className="text-sm font-medium mt-1">{r.title}</div>}
            <div className="text-sm text-[#374151] mt-0.5 whitespace-pre-line">{r.text}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

const ThemeList = ({ items, max, muted }) => (
  <div className="mt-3 space-y-2">
    {items.map(([t, n]) => (
      <div key={t} className="flex items-center gap-3 text-sm">
        <span className={`flex-1 ${muted ? 'text-[#6B7280]' : 'text-[#374151]'}`}>{t}</span>
        <span className="w-16 h-1.5 bg-[#F3F4F6] rounded">
          <span className={`block h-1.5 rounded ${muted ? 'bg-[#A5B4FC]' : 'bg-[#436AEB]'}`} style={{ width: `${(n / max) * 100}%` }} />
        </span>
        <span className="w-8 text-right text-[#6B7280]">~{n}</span>
      </div>
    ))}
  </div>
);

const Kpi = ({ label, value, accent }) => (
  <div>
    <div className="text-[11px] text-[#6B7280]">{label}</div>
    <div className={`text-lg font-semibold mt-0.5 ${accent ? 'text-[#DC2626]' : ''}`}>{value}</div>
  </div>
);

const ScoreBar = ({ d, total }) => {
  const colors = { 1: '#EF4444', 2: '#F97316', 3: '#D1D5DB', 4: '#A5B4FC', 5: '#436AEB' };
  return (
    <div className="mt-4">
      <div className="flex h-2.5 rounded overflow-hidden">
        {[1, 2, 3, 4, 5].map(s => (
          <div key={s} style={{ width: `${(d[s] / total) * 100}%`, background: colors[s] }} title={`${s}★: ${d[s]}`} />
        ))}
      </div>
      <div className="flex justify-between text-[11px] text-[#6B7280] mt-1">
        {[1, 2, 3, 4, 5].map(s => <span key={s}>{s}★ %{Math.round((d[s] / total) * 100)}</span>)}
      </div>
    </div>
  );
};
